// Reactive drag-to-reorder state + handlers shared by TreeTable and
// ClickableTable. Caller supplies the read/write hooks for column order;
// we own the drag state.
export function createColumnDrag(opts: {
  getColumnOrder: () => string[]
  setColumnOrder: (order: string[]) => void
}) {
  let draggedColumn = $state<string | null>(null)
  let dropTargetName = $state<string | null>(null)
  let dropAfter = $state<boolean>(false)

  function onDragStart(e: DragEvent, name: string) {
    if (!e.dataTransfer) return
    draggedColumn = name
    e.dataTransfer.effectAllowed = "move"
    e.dataTransfer.setData("text/plain", name)
  }

  // Right-half-of-N and left-half-of-(N+1) both insert between them.
  function onDragOver(e: DragEvent, name: string) {
    if (!draggedColumn || draggedColumn === name) return
    e.preventDefault()
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    dropTargetName = name
    dropAfter = e.clientX - rect.left > rect.width / 2
  }

  function onDragLeave(name: string) {
    if (dropTargetName === name) dropTargetName = null
  }

  function onDrop(e: DragEvent, name: string) {
    e.preventDefault()
    const source = draggedColumn
    const landAfter = dropAfter
    cleanupDrag()
    if (!source || source === name) return
    const current = [...opts.getColumnOrder()]
    const fromIdx = current.indexOf(source)
    if (fromIdx === -1) return
    current.splice(fromIdx, 1)
    let toIdx = current.indexOf(name)
    if (toIdx === -1) return
    if (landAfter) toIdx += 1
    current.splice(toIdx, 0, source)
    opts.setColumnOrder(current)
  }

  function cleanupDrag() {
    draggedColumn = null
    dropTargetName = null
    dropAfter = false
  }

  function columnClasses(name: string): string {
    const classes: string[] = []
    if (draggedColumn === name) classes.push("bg-blue-100/70")
    if (dropTargetName === name && draggedColumn !== name) {
      classes.push(
        dropAfter
          ? "column-drop-indicator-after"
          : "column-drop-indicator-before"
      )
    }
    return classes.join(" ")
  }

  return {
    get draggedColumn() {
      return draggedColumn
    },
    onDragStart,
    onDragOver,
    onDragLeave,
    onDrop,
    cleanupDrag,
    columnClasses,
  }
}
